// LEGACY_CJS_REMOVE: ENG-601 — Delete after web, CLI, API validation and test callers migrate.
// Host-side compiler for guest source. Guest code NEVER executes in the host
// realm: sucrase transpiles TSX to a CJS module STRING (fast validation, no
// execution), which the host hands to the sandboxed iframe over guest-init.
//
// Enforcement layering, strongest first:
//   1. iframe sandbox + CSP: no fetch/XHR/WebSocket, no cookies, no parent DOM.
//   2. frame require map: unknown imports throw at execution time.
//   3. the allowlist scan below: fast, friendly compile-time feedback.
import { transform } from 'sucrase'

import { ALLOWED_GUEST_IMPORTS } from '../protocol.ts'

export type CompileGuestSourceResult = { code: string; error?: undefined } | { error: Error }

const REQUIRE_SPECIFIER_PATTERN = /\brequire\((['"])([^'"]+)\1\)/g

/**
 * Compile guest source to a CJS module string and reject sources importing
 * anything outside the allowlist. Does NOT execute the code.
 */
export const compileGuestSource = (source: string): CompileGuestSourceResult => {
  try {
    const { code } = transform(source, {
      transforms: ['typescript', 'jsx', 'imports'],
      filePath: 'guest-source.tsx',
      jsxRuntime: 'automatic',
      // Emit jsxDEV source arguments; the frame's private jsx-dev-runtime consumes them while
      // still rendering through React's production runtime. Agentation reads the resulting fiber
      // metadata and reports exact lines in the runtime source users are editing.
      production: false,
    })
    for (const match of code.matchAll(REQUIRE_SPECIFIER_PATTERN)) {
      const specifier = match[2]
      if (specifier !== undefined && !ALLOWED_GUEST_IMPORTS.has(specifier)) {
        throw new Error(`Unsupported import: ${specifier}`)
      }
    }
    if (!/module\.exports|exports\s*\.\s*default|exports\.default/.test(code)) {
      throw new Error('Guest source must export a default React component')
    }
    return { code }
  } catch (error: unknown) {
    return { error: error instanceof Error ? error : new Error('Failed to compile guest source') }
  }
}
